'use client';
import { XYChartBarSpark } from './XYChartBarSpark';
import { XYChartFadedSparkSeries } from './XYChartFadedSparkSeries';
import { XYChartLineSpark } from './XYChartLineSpark';
import { COMPACT_PREVIEW_SPARK_SVG_STYLE } from './xychart-compact-preview-styles';
import type { XYChartPreviewSeries } from './xychart-compact-preview-model';
import { selectFadedSparkSeriesKey, selectXYChartSparklineModel, } from './xychart-sparkline-model';
const LATEST_CATEGORY_VALUE = "latest-category";
export interface XYChartSparklineProps {
    width: number;
    height: number;
    chartIdentity: string;
    focusSeries: XYChartPreviewSeries;
    fadedSeries: XYChartPreviewSeries[];
    trendColor: string;
    categoryLabels: string[];
}
const SPARKLINE_GRADIENT_DEFS = 'sparklineGradientDefs';
const SPARKLINE_FOCUS_GRADIENT_NAME = 'sparklineFocusFillGradient';
const SPARKLINE_LINE_GRADIENT_NAME = 'sparklineLineGradient';
const SPARKLINE_BASE_GRADIENT_NAME = 'sparklineBaseLineGradient';
const SPARKLINE_BASELINE_NAME = 'sparklineBaseAxisLine';
const SPARKLINE_GRADIENT_STOP_NAME = 'sparklineGradientStop';
function ensureSafeDebugId(input: string): string {
    return input.trim().replace(/[^a-zA-Z0-9_-]/g, '-');
}
export function XYChartSparkline({ width, height, chartIdentity, focusSeries, fadedSeries, trendColor, categoryLabels, }: XYChartSparklineProps) {
    const safeChartIdentity = ensureSafeDebugId(chartIdentity);
    const model = selectXYChartSparklineModel({
        width,
        height,
        focusSeries,
        fadedSeries,
    });
    const focusGradientId = `${safeChartIdentity}-spark-focus-gradient`;
    const lineGradientId = `${safeChartIdentity}-spark-line-gradient`;
    if (!model)
        return null;
    const baseGradientId = `${safeChartIdentity}-spark-base-gradient`;
    const latestCategoryLabel = categoryLabels[focusSeries.data.length - 1] ?? LATEST_CATEGORY_VALUE;
    const latestPointIdentity = `${focusSeries.label}-${latestCategoryLabel}`;
    return (
      <svg data-el="Cmarkdownxychart/Sparkline/Root" width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={COMPACT_PREVIEW_SPARK_SVG_STYLE}>
        <defs>
          <linearGradient id={focusGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={trendColor} stopOpacity={0.35}/>
            <stop offset="100%" stopColor={trendColor} stopOpacity={0}/>
          </linearGradient>
          <linearGradient id={lineGradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={trendColor} stopOpacity={0.2}/>
            <stop offset="100%" stopColor={trendColor} stopOpacity={1}/>
          </linearGradient>
          <linearGradient id={baseGradientId}>
            <stop offset="0%" stopColor="rgba(255,255,255,0)"/>
            <stop offset="20%" stopColor="rgba(255,255,255,0.15)"/>
            <stop offset="80%" stopColor="rgba(255,255,255,0.15)"/>
            <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
          </linearGradient>
        </defs>

        <line x1={0} y1={model.zeroY} x2={width} y2={model.zeroY} stroke={`url(#${baseGradientId})`} strokeWidth={1}/>

        {fadedSeries.map((series) => (<XYChartFadedSparkSeries key={selectFadedSparkSeriesKey({ series })} series={series} stepX={model.stepX} xPositionFor={model.xPositionFor} yPositionFor={model.yPositionFor} zeroY={model.zeroY} categoryLabels={categoryLabels}/>))}

        {model.isBarChart ? (<XYChartBarSpark stepX={model.stepX} focusSeries={focusSeries} categoryLabels={categoryLabels} xPositionFor={model.xPositionFor} yPositionFor={model.yPositionFor} zeroY={model.zeroY} trendColor={trendColor}/>) : (<XYChartLineSpark focusAreaPath={model.focusAreaPath} focusLinePath={model.focusLinePath} focusGradientId={focusGradientId} lineGradientId={lineGradientId} lastPoint={model.lastPoint} latestPointIdentity={latestPointIdentity} height={height} trendColor={trendColor}/>)}
      </svg>
    );
}
